"use client";

import { useApolloClient, useLazyQuery } from "@apollo/client/react";
import type { DeepPartial } from "@apollo/client/utilities";
import { memo, useEffect, useRef, useState } from "react";

import { SinglePostDetails } from "@/components/app/post/single-post/single-post-details";
import { DelegatedLink } from "@/components/shared/delegated-link";
import { Spinner } from "@/components/shared/spinner";
import { PostContextProvider, usePostContext } from "@/contexts/post-context";
import { type Post, PostByPostHashDocument } from "@/graphql/codegen/graphql";
import { useBooleanState } from "@/hooks/use-boolean-state";
import { isAbortError } from "@/utils/helpers";

export const SinglePostContent = memo(() => {
  const [loading, { setFalse: unsetLoading }] = useBooleanState(true);
  const [parentPosts, setParentPosts] = useState<DeepPartial<Post>[]>([]);
  const { post, setRootPostHash, rootPostHash } = usePostContext();
  const mainPostRef = useRef<HTMLDivElement>(null);
  const apolloClient = useApolloClient();

  const [getPost] = useLazyQuery(PostByPostHashDocument);

  useEffect(() => {
    let cancelled = false;

    const getParentPostByHash = async (parentPostHash: string) => {
      try {
        const parentPosts: DeepPartial<Post>[] = [];

        while (true) {
          if (cancelled) {
            return null;
          }

          const request = getPost({
            variables: {
              postHash: parentPostHash,
            },
          });
          request.retain?.();
          const { data } = await request;
          const post = data?.postByPostHash;

          if (!post) break;

          const nextParentPostHash = post.parentPost?.postHash;
          parentPosts.unshift(post);

          if (!nextParentPostHash) {
            if (!cancelled) {
              setRootPostHash(post.postHash);
            }
            break;
          } else {
            parentPostHash = nextParentPostHash;
          }
        }

        if (!cancelled) {
          setParentPosts(parentPosts);
        }
      } catch (error) {
        if (!isAbortError(error)) {
          console.error(error);
        }
        return null;
      }
    };

    const loadParentPosts = async () => {
      const parentPostHash = post.parentPost?.postHash;
      if (parentPostHash && !parentPosts.length) {
        await getParentPostByHash(parentPostHash);
        unsetLoading();
      } else {
        unsetLoading();
      }
    };

    loadParentPosts();

    return () => {
      cancelled = true;
    };
  }, [
    getPost,
    parentPosts.length,
    post.parentPost?.postHash,
    post?.postHash,
    rootPostHash,
    setRootPostHash,
    unsetLoading,
  ]);

  useEffect(() => {
    if (!loading && mainPostRef.current) {
      setTimeout(() => {
        mainPostRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
    }

    if (!loading && !parentPosts.length && !rootPostHash) {
      setRootPostHash(post?.postHash ?? "");
    }
  }, [
    loading,
    parentPosts.length,
    post?.postHash,
    rootPostHash,
    setRootPostHash,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {parentPosts.map((parentPost) => (
        <PostContextProvider
          key={parentPost.postHash}
          {...{
            post: parentPost,
            showMoreMaxHeight: 300,
            disableReadMore: false,
            updatePostByHash: (hash, post) => {
              setParentPosts((prev) => {
                return prev.map((prevPost) => {
                  if (prevPost.postHash === hash) {
                    const newPost =
                      typeof post === "function" ? post(prevPost) : post;
                    try {
                      apolloClient.cache.modify({
                        id: apolloClient.cache.identify(newPost),
                        // @ts-expect-error - This is a workaround for a bug in Apollo Client
                        fields: newPost,
                      });
                    } catch {
                      /* we don't care if it throws error */
                    }
                    return newPost;
                  }
                  return prevPost;
                });
              });
            },
          }}
        >
          <DelegatedLink href={`/post/${parentPost.postHash}`}>
            <SinglePostDetails post={parentPost} isParentPost />
          </DelegatedLink>
        </PostContextProvider>
      ))}
      <div ref={mainPostRef}>
        <SinglePostDetails post={post} key="main-post" />
      </div>
    </>
  );
});

SinglePostContent.displayName = "SinglePostContent";
